import React, { useRef, useState, useEffect } from "react";
import { FaPlayCircle, FaPauseCircle, FaVolumeUp } from "react-icons/fa";
import "./CustomAudioPlayer.css";

const speedOptions = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

// `onPlayIntent(currentTime)`: when provided, pressing play defers to the
// caller instead of starting playback here — the details panel uses it to open
// the expanded player with the transcript. It receives this player's position
// so a seek made here carries across; without that, seeking in the compact bar
// and then pressing play expanded the player back at the start.
// `onSeek(seconds)`: reports committed seeks, so the position survives even if
// the expanded view is opened some other way (the section header).
// `initialTime`: resume point applied once metadata is known, so collapsing
// the expanded player hands back a bar showing where listening stopped.
// All three are optional; callers that pass nothing get the original player.
// onSeek fires when the user commits a seek; onProgress on every timeupdate
// while playing. Between them the parent can hold the current position, so
// another view (the expanded player) can take over from exactly here.
function CustomAudioPlayer({ src, onSeek, onProgress, initialTime = 0 }) {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [seekValue, setSeekValue] = useState(0); // for slider UI
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  // Frozen at mount, applied once: a resume point describes where playback
  // stood when this player appeared, not whatever the parent renders later.
  const initialTimeRef = useRef(initialTime);
  const resumeAppliedRef = useRef(false);
  // Latest callback without re-subscribing the media listeners.
  const onProgressRef = useRef(onProgress);
  useEffect(() => {
    onProgressRef.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    const audio = audioRef.current;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      const resume = initialTimeRef.current;
      if (
        !resumeAppliedRef.current &&
        resume > 0.5 &&
        audio.duration &&
        resume < audio.duration
      ) {
        resumeAppliedRef.current = true;
        audio.currentTime = resume;
        setCurrentTime(resume);
        setSeekValue((resume / audio.duration) * 100);
      }
    };

    const updateProgress = () => {
      if (!audio.seeking) {
        setCurrentTime(audio.currentTime);
        if (duration) {
          setSeekValue(Math.min(100, (audio.currentTime / duration) * 100));
        }
        onProgressRef.current?.(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      // Playback has proven the real length; every estimate loses to it.
      if (Number.isFinite(audio.currentTime) && audio.currentTime > 0) {
        setDuration(audio.currentTime);
      }
    };

    // An MP3's header duration is an estimate the element may never revise
    // mid-play. The seekable range's end is its live knowledge of how much
    // audio actually exists, so the total is the best of every signal,
    // adopted monotonically as data loads.
    const adoptDuration = () => {
      let d = Number.isFinite(audio.duration) ? audio.duration : 0;
      try {
        if (audio.seekable && audio.seekable.length > 0) {
          d = Math.max(d, audio.seekable.end(audio.seekable.length - 1));
        }
      } catch {
        /* a mid-load seekable range can throw; keep what we have */
      }
      if (d > 0) setDuration((prev) => Math.max(prev, d));
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("durationchange", adoptDuration);
    audio.addEventListener("progress", adoptDuration);
    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("durationchange", adoptDuration);
      audio.removeEventListener("progress", adoptDuration);
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [duration]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  };

  const handleSeekPreview = (e) => {
    const progress = parseFloat(e.target.value);
    if (!isNaN(progress)) {
      setSeekValue(progress);
    }
  };

  // On release: commit actual seek
  const handleSeekCommit = (e) => {
    const progress = parseFloat(e.target.value);
    if (progress === 0 && currentTime > 1) {
      return;
    }

    if (!isNaN(progress) && duration) {
      const newTime = (progress / 100) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      setSeekValue(progress);
      onSeek?.(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (audio.paused) {
      audio.play();
      setIsPlaying(true);
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <div className="custom-audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="controls-container">
        <button
          onClick={togglePlayPause}
          className={`play-pause-button ${isPlaying ? "pause" : "play"}`}
        >
          {isPlaying ? <FaPauseCircle size={20} /> : <FaPlayCircle size={20} />}
        </button>
        <FaVolumeUp size={16} className="volume-icon" />
        <input
          type="range"
          className="volume-slider"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolumeChange}
        />
        <span className="volume-percentage">{Math.round(volume * 100)}%</span>
      </div>
      <div className="progress-container">
        <input
          ref={progressRef}
          type="range"
          className="progress-bar"
          min="0"
          max="100"
          step="0.1"
          value={seekValue}
          onInput={handleSeekPreview}
          onChange={handleSeekCommit} // on release
        />
        <span>
          {/* The total holds still; the elapsed clock is clamped to it. Two
              numbers running together reads as a broken timer, so if the file
              outlives every estimate, elapsed waits at the total until
              `ended` corrects the total exactly. */}
          {formatTime(
            duration > 0 ? Math.min(currentTime, duration) : currentTime,
          )}{" "}
          / {formatTime(duration)}
        </span>
      </div>
      <div className="playback-rate-container">
        <span>Playback Speed:</span>
        <select
          className="speed-dropdown"
          value={playbackRate}
          onChange={(e) => handlePlaybackRateChange(parseFloat(e.target.value))}
        >
          {speedOptions.map((speed) => (
            <option key={speed} value={speed}>
              {speed}x
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default CustomAudioPlayer;
